/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { Image, Heading, Flex, View, IllustratedMessage, ProgressCircle } from '@adobe/react-spectrum';
import { Card, CardView, GridLayout } from '@react-spectrum/card';
import { Size } from '@react-stately/virtualizer';
import NotFound from '@spectrum-icons/illustrations/NotFound';

import classes from './ImageGallery.scss';

const messages = defineMessages({
    labelLoading: {
        id: 'label.loading',
        defaultMessage: 'Loading…'
    },
    labelGalleryOfProductImages: {
        id: 'label.galleryOfProductImages',
        defaultMessage: 'Gallery of product images'
    },
    errorNoImageAvailable: {
        id: 'error.noImageAvailable',
        defaultMessage: 'No image available.'
    }
});

/**
 * Image carousel with one main image displayed at the top with cards of all available images below.
 */
const ImageGallery = props => {
    const { images, loading } = props;
    const { formatMessage } = useIntl();

    // Currently selected image
    const [currentIndex, setCurrentIndex] = useState(0);

    let image;
    if (loading) {
        image = (
            <IllustratedMessage>
                <ProgressCircle size="L" aria-label={formatMessage(messages.labelLoading)} isIndeterminate />
            </IllustratedMessage>
        );
    } else if (images && images.length > 0) {
        image = (
            <Image
                data-test-id="product-image"
                objectFit="contain"
                src={images[currentIndex].url}
                alt={images[currentIndex].label}
            />
        );
    } else {
        image = (
            <IllustratedMessage>
                <NotFound />
                <Heading>{formatMessage(messages.errorNoImageAvailable)}</Heading>
            </IllustratedMessage>
        );
    }

    let cardView = null;
    if (!loading && images && images.length > 1) {
        // Layout for CardView
        const layout = new GridLayout({
            itemPadding: 16,
            minItemSize: new Size(72, 72),
            maxItemSize: new Size(200, 200),
            margin: 32,
            minSpace: new Size(15, 15),
            maxColumns: 4
        });

        const renderCard = ({ key, label, url }) => (
            <Card key={key} textValue={label}>
                <Image src={url} />
            </Card>
        );

        const onSelectionChange = index => {
            if (index.size > 0) {
                setCurrentIndex(parseInt([...index][0]));
            }
        };

        // Add key to each image
        const items = images.map((image, index) => ({ ...image, key: index }));

        cardView = (
            <div className={classes.cardView}>
                <CardView
                    items={items}
                    selectedKeys={[currentIndex.toString()]}
                    selectionMode="single"
                    isQuiet
                    layout={layout}
                    aria-label={formatMessage(messages.labelGalleryOfProductImages)}
                    onSelectionChange={onSelectionChange}
                >
                    {renderCard}
                </CardView>
            </div>
        );
    }

    return (
        <>
            <View backgroundColor="gray-200" UNSAFE_style={{ aspectRatio: '1 / 1' }}>
                <Flex justifyContent="center" width="100%" height="100%">
                    {image}
                </Flex>
            </View>
            {cardView}
        </>
    );
};

ImageGallery.propTypes = {
    loading: PropTypes.bool,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            url: PropTypes.string
        })
    )
};

export default ImageGallery;
